import React, { useContext, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Box, Divider, ListItem, ListItemText, Popover } from "@mui/material";
import { CommonDataContext } from "context/CommonDataContext";
import { v4 as uuidv4 } from "uuid";
import {
  INFANT_UPPER_LIMIT,
  MIN_PAX_VALUE,
  TOTAL_PAX_COUNT_LIMIT,
  UNACCOMPANIED_MINOR_LIMIT,
  ZERO_VALUE
} from "../../constants/variables";
import PaxCategories from "../../containers/PaxCategories";
import { extractResource } from "../../helper/extractResource";
import orangeDownArrow from "../../public/svg/orangeDownArrow.svg";
import style from "../MobileSearchPanel/Booking.module.css";
import { Button } from "../UI/Button";

function ConfirmationDialogRaw(props: any) {
  const { onClose, value, setValue, open, ...other } = props;
  const radioGroupRef = useRef(null);
  const [totalPaxCount, setTotalPaxCount] = useState(1);
  const studentSpecialFareText = "Student";
  const umnrSpecialFareText = "Unaccompanied Minor";
  const armedForceSpeicalFareText = "Armed Forces";
  const medicalProSpecialFareText = "Medical Professionals";
  const seniorCitizenSpecialFareText = "Senior Citizen";
  const { resourceData } = props;
  const { webConfig }: any = useContext(CommonDataContext);
  const enableExtraSeat = webConfig?.enableExtraSeat;
  const [updateCount, setUpdateCount] = useState(0);
  const { paxTypesCount: paxTypesCountGlobal, setPaxTypesCount } = props;
  const [paxTypesCount, setPaxTypesCountLocal] = useState({
    ADT: {
      ...paxTypesCountGlobal?.ADT,
      ageGroup: extractResource(resourceData, "adults"),
      ageInfo: extractResource(resourceData, "adultAgeGroup")
    },
    CHD: {
      ...paxTypesCountGlobal?.CHD,
      ageGroup: extractResource(resourceData, "children"),
      ageInfo: extractResource(resourceData, "childrenAgeGroup")
    },
    SNR: {
      ...paxTypesCountGlobal?.SNR,
      ageGroup: extractResource(resourceData, "seniorCitizenPassengerType"),
      ageInfo: extractResource(resourceData, "seniorCitizenAgeGroup")
    },
    INF: {
      ...paxTypesCountGlobal?.INF,
      ageGroup: extractResource(resourceData, "infants"),
      ageInfo: extractResource(resourceData, "infantAgeGroup")
    },
    EXST: {
      ...paxTypesCountGlobal?.EXST,
      ageGroup: extractResource(resourceData, "extraSeats"),
      ageInfo: extractResource(resourceData, "bookTheExtraSeatNextToYou")
    }
  });

  const handleEntering = () => {
    if (radioGroupRef.current != null) {
      radioGroupRef.current.focus();
    }
  };

  const getFinalString = (data?: any) => {
    let adultValue;
    let childValue;
    let seniorcitizen;
    let infantValue;
    let extraSeat;

    if (data) {
      adultValue = data.ADT?.value || 0; // Use optional chaining and default to 0 if value is null or undefined
      childValue = data.CHD?.value || 0;
      seniorcitizen = data.SNR?.value || 0;
      infantValue = data.INF?.value || 0;
      extraSeat = data.EXST?.value || 0;
    } else {
      adultValue = enableExtraSeat ? paxTypesCount.ADT?.value || 0 : 0;
      childValue = enableExtraSeat ? paxTypesCount.CHD?.value || 0 : 0;
      seniorcitizen = enableExtraSeat ? paxTypesCount.SNR?.value || 0 : 0;
      infantValue = enableExtraSeat ? paxTypesCount.INF?.value || 0 : 0;
      extraSeat = enableExtraSeat ? paxTypesCount.EXST?.value || 0 : 0;
    }

    let finalString = "";
    if (adultValue > 0) {
      finalString = `${adultValue} ${extractResource(resourceData, "adultPassengerType")}`;
      if (adultValue > 1) {
        finalString = `${adultValue} ${extractResource(resourceData, "adults")}`;
      }
    }

    if (childValue > 0) {
      if (adultValue > 0) {
        finalString += ", ";
      }
      if (childValue > 1) {
        finalString += `${childValue} ${extractResource(resourceData, "children")}`;
      } else {
        finalString += `${childValue} ${extractResource(resourceData, "childPassengerType")}`;
      }
    }

    if (seniorcitizen > 0) {
      if (finalString.length > 1) {
        finalString += ", ";
      }
      if (seniorcitizen > 0) {
        finalString += `${seniorcitizen} ${extractResource(resourceData, "seniorCitizenShort")}`;
      }
    }
    if (infantValue > 0) {
      if (finalString.length > 1) {
        finalString += ", ";
      }
      if (infantValue > 0) {
        finalString += `${infantValue} ${extractResource(resourceData, "infantPassengerType")}`;
      }
    }

    if (extraSeat > 0 && enableExtraSeat) {
      if (finalString.length > 0) {
        finalString += ", ";
      }
      finalString += `${extraSeat} ${extractResource(resourceData, "ES")}`;
    }
    return finalString;
  };

  const handleOk = (event: React.MouseEvent<HTMLButtonElement> | null): void => {
    if (typeof event !== "undefined" && event != null) {
      event.stopPropagation();
    }

    const adultValue = paxTypesCount.ADT?.value || 0; // Ensure it's defined or use a default value (0 in this case)
    const childValue = paxTypesCount.CHD?.value || 0;
    const seniorcitizen = paxTypesCount.SNR?.value || 0;
    const infantValue = paxTypesCount.INF?.value || 0;
    const extraSeat = enableExtraSeat ? paxTypesCount.EXST?.value || 0 : 0; // Check the flag

    const finalString = getFinalString();

    onClose(finalString);

    props.setPassenger({
      child: childValue,
      adult: adultValue,
      seniorcitizen,
      infant: infantValue,
      extraSeat: extraSeat,
      total: childValue + adultValue + seniorcitizen + extraSeat
    });
  };

  const handleCancel = (event) => {
    event.stopPropagation();
    handleOk(event);
  };

  useEffect(() => {
    localStorage.setItem("value", JSON.stringify(value));
    const finalString = getFinalString();
    setValue(finalString);
  }, []);

  const speicalFareOfStudent = () => {
    const retainedStudentData = {
      ADT: {
        ...paxTypesCount.ADT,
        ageGroup: extractResource(resourceData, "adults"),
        ageInfo: extractResource(resourceData, "adultAgeGroup")
      },
      CHD: {
        ...paxTypesCount.CHD,
        ageGroup: extractResource(resourceData, "children"),
        ageInfo: extractResource(resourceData, "childrenAgeGroup")
      },
      SNR: {
        ageGroup: extractResource(resourceData, "seniorCitizenPassengerType"),
        ageInfo: extractResource(resourceData, "seniorCitizenAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: true,
        isDescDisable: true
      },
      INF: {
        ...paxTypesCount.INF,
        ageGroup: extractResource(resourceData, "infants"),
        ageInfo: extractResource(resourceData, "infantAgeGroup")
      },
      EXST: {
        ...paxTypesCount.EXST,
        ageGroup: extractResource(resourceData, "extraSeats"),
        ageInfo: extractResource(resourceData, "bookTheExtraSeatNextToYou")
      }
    };

    const studentDataReset = {
      ADT: {
        ageGroup: extractResource(resourceData, "adults"),
        ageInfo: extractResource(resourceData, "adultAgeGroup"),
        value: 1,
        isIncDisable: false,
        isDescDisable: true
      },
      CHD: {
        ageGroup: extractResource(resourceData, "children"),
        ageInfo: extractResource(resourceData, "childrenAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: false,
        isDescDisable: true
      },
      SNR: {
        ageGroup: extractResource(resourceData, "seniorCitizenPassengerType"),
        ageInfo: extractResource(resourceData, "seniorCitizenAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: true,
        isDescDisable: true
      },
      INF: {
        ageGroup: extractResource(resourceData, "infants"),
        ageInfo: extractResource(resourceData, "infantAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: false,
        isDescDisable: true
      },
      EXST: {
        ageGroup: extractResource(resourceData, "extraSeats"),
        ageInfo: extractResource(resourceData, "bookTheExtraSeatNextToYou"),
        value: ZERO_VALUE,
        isIncDisable: false,
        isDescDisable: true
      }
    };

    let data;

    if (!props.prevSpecialFareForPax?.current) {
      data = retainedStudentData;
    } else {
      data = studentDataReset;
    }

    setPaxTypesCount(data);
    setPaxTypesCountLocal(data);
    setUpdateCount(updateCount + 1); // just to make the view update
    const finalString = getFinalString(data);
    setValue(finalString);

    const paxData = {
      child: data.CHD.value,
      adult: data.ADT.value,
      seniorcitizen: data.SNR.value,
      infant: data.INF.value,
      extraSeat: data.EXST.value,
      total: data.CHD.value + data.ADT.value + data.SNR.value + data.EXST.value
    };

    props.setPassenger(paxData);
  };

  const resetPaxTypeCount = () => {
    const resetData = {
      ADT: {
        ageGroup: extractResource(resourceData, "adults"),
        ageInfo: extractResource(resourceData, "adultAgeGroup"),
        isIncDisable: false,
        isDescDisable: true,
        value: MIN_PAX_VALUE
      },
      CHD: {
        ageGroup: extractResource(resourceData, "children"),
        ageInfo: extractResource(resourceData, "childrenAgeGroup"),
        isIncDisable: false,
        isDescDisable: true,
        value: ZERO_VALUE
      },
      SNR: {
        ageGroup: extractResource(resourceData, "seniorCitizenPassengerType"),
        ageInfo: extractResource(resourceData, "seniorCitizenAgeGroup"),
        isIncDisable: false,
        isDescDisable: true,
        value: ZERO_VALUE
      },
      INF: {
        ageGroup: extractResource(resourceData, "infants"),
        ageInfo: extractResource(resourceData, "infantAgeGroup"),
        isIncDisable: false,
        isDescDisable: true,
        value: ZERO_VALUE
      },
      EXST: {
        ageGroup: extractResource(resourceData, "extraSeats"),
        ageInfo: extractResource(resourceData, "bookTheExtraSeatNextToYou"),
        isIncDisable: false,
        isDescDisable: true,
        value: ZERO_VALUE
      }
    };

    setPaxTypesCount(resetData);
    setPaxTypesCountLocal(resetData);
    setUpdateCount(updateCount + 1); // just to make the view update
    setValue(getFinalString(resetData));
    props.setSpecialFareForPax();

    const paxData = {
      child: resetData.CHD.value,
      adult: resetData.ADT.value,
      seniorcitizen: resetData.SNR.value,
      infant: resetData.INF.value,
      extraSeat: resetData.EXST.value,
      total: resetData.CHD.value + resetData.ADT.value + resetData.SNR.value + resetData.EXST.value
    };
    props.setPassenger(paxData);
  };

  const specialFareForUNMR = () => {
    const resetUNMRData = {
      ADT: {
        ageGroup: extractResource(resourceData, "adults"),
        ageInfo: extractResource(resourceData, "adultAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: true,
        isDescDisable: true
      },
      CHD: {
        ageGroup: extractResource(resourceData, "children"),
        ageInfo: extractResource(resourceData, "childrenAgeGroup"),
        value: MIN_PAX_VALUE,
        isIncDisable: false,
        isDescDisable: true
      },
      SNR: {
        ageGroup: extractResource(resourceData, "seniorCitizenPassengerType"),
        ageInfo: extractResource(resourceData, "seniorCitizenAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: true,
        isDescDisable: true
      },
      INF: {
        ageGroup: extractResource(resourceData, "infants"),
        ageInfo: extractResource(resourceData, "infantAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: true,
        isDescDisable: true
      },
      EXST: {
        ageGroup: extractResource(resourceData, "extraSeats"),
        ageInfo: extractResource(resourceData, "bookTheExtraSeatNextToYou"),
        value: ZERO_VALUE,
        isIncDisable: true,
        isDescDisable: true
      }
    };

    const retainedUNMRData = {
      ADT: {
        ageGroup: extractResource(resourceData, "adults"),
        ageInfo: extractResource(resourceData, "adultAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: true,
        isDescDisable: true
      },
      CHD: {
        ...paxTypesCount.CHD,
        ageGroup: extractResource(resourceData, "children"),
        ageInfo: extractResource(resourceData, "childrenAgeGroup")
      },
      SNR: {
        ageGroup: extractResource(resourceData, "seniorCitizenPassengerType"),
        ageInfo: extractResource(resourceData, "seniorCitizenAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: true,
        isDescDisable: true
      },
      INF: {
        ageGroup: extractResource(resourceData, "infants"),
        ageInfo: extractResource(resourceData, "infantAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: true,
        isDescDisable: true
      },
      EXST: {
        ageGroup: extractResource(resourceData, "extraSeats"),
        ageInfo: extractResource(resourceData, "bookTheExtraSeatNextToYou"),
        value: ZERO_VALUE,
        isIncDisable: true,
        isDescDisable: true
      }
    };

    let UNMRData;

    if (!props.prevSpecialFareForPax?.current) {
      UNMRData = retainedUNMRData;
    } else {
      UNMRData = resetUNMRData;
    }

    setPaxTypesCount(UNMRData);
    setPaxTypesCountLocal(UNMRData);
    setUpdateCount(updateCount + 1); // just to make the view update
    setValue(getFinalString(UNMRData));
    const paxData = {
      child: UNMRData.CHD.value,
      adult: UNMRData.ADT.value,
      seniorcitizen: UNMRData.SNR.value,
      infant: UNMRData.INF.value,
      extraSeat: UNMRData.EXST.value,
      total: UNMRData.CHD.value + UNMRData.ADT.value + UNMRData.SNR.value + UNMRData.EXST.value
    };
    props.setPassenger(paxData);
  };

  const specialFareForSNR = () => {
    const retainedSNRData = {
      ADT: {
        ...paxTypesCount.ADT,
        ageGroup: extractResource(resourceData, "adults"),
        ageInfo: extractResource(resourceData, "adultAgeGroup")
      },
      CHD: {
        ...paxTypesCount.CHD,
        ageGroup: extractResource(resourceData, "children"),
        ageInfo: extractResource(resourceData, "childrenAgeGroup")
      },
      SNR: {
        ...paxTypesCount.SNR,
        ageGroup: extractResource(resourceData, "seniorCitizenPassengerType"),
        ageInfo: extractResource(resourceData, "seniorCitizenAgeGroup")
      },
      INF: {
        ...paxTypesCount.INF,
        ageGroup: extractResource(resourceData, "infants"),
        ageInfo: extractResource(resourceData, "infantAgeGroup")
      },
      EXST: {
        ...paxTypesCount.EXST,
        ageGroup: extractResource(resourceData, "extraSeats"),
        ageInfo: extractResource(resourceData, "bookTheExtraSeatNextToYou")
      }
    };

    const resetSNRData = {
      ADT: {
        ...paxTypesCount.ADT,
        ageGroup: extractResource(resourceData, "adults"),
        ageInfo: extractResource(resourceData, "adultAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: false,
        isDescDisable: true
      },
      CHD: {
        ageGroup: extractResource(resourceData, "children"),
        ageInfo: extractResource(resourceData, "childrenAgeGroup"),
        ...paxTypesCount.CHD,
        value: ZERO_VALUE,
        isIncDisable: false,
        isDescDisable: true
      },
      SNR: {
        ...paxTypesCount.SNR,
        ageGroup: extractResource(resourceData, "seniorCitizenPassengerType"),
        ageInfo: extractResource(resourceData, "seniorCitizenAgeGroup"),
        value: 1,
        isIncDisable: false,
        isDescDisable: true
      },
      INF: {
        ...paxTypesCount.INF,
        ageGroup: extractResource(resourceData, "infants"),
        ageInfo: extractResource(resourceData, "infantAgeGroup"),
        value: ZERO_VALUE,
        isIncDisable: false,
        isDescDisable: true
      },
      EXST: {
        ...paxTypesCount.EXST,
        ageGroup: extractResource(resourceData, "extraSeats"),
        ageInfo: extractResource(resourceData, "bookTheExtraSeatNextToYou"),
        value: ZERO_VALUE,
        isIncDisable: false,
        isDescDisable: true
      }
    };

    let SNRData;
    if (!props?.prevSpecialFareForPax?.current) {
      SNRData = retainedSNRData;
    } else {
      SNRData = resetSNRData;
    }

    setPaxTypesCount(SNRData);
    setPaxTypesCountLocal(SNRData);
    setUpdateCount(updateCount + 1); // just to make the view update
    setValue(getFinalString(SNRData));
    const paxData = {
      child: SNRData.CHD.value,
      adult: SNRData.ADT.value,
      seniorcitizen: SNRData.SNR.value,
      infant: SNRData.INF.value,
      extraSeat: SNRData.EXST.value,
      total: SNRData.CHD.value + SNRData.ADT.value + SNRData.SNR.value + SNRData.EXST.value
    };
    props.setPassenger(paxData);
  };

  const checkforSpecialFare = () => {
    if (
      props.specialFareForPax == studentSpecialFareText ||
      props.specialFareForPax == armedForceSpeicalFareText ||
      props.specialFareForPax == medicalProSpecialFareText
    ) {
      // if it is student, only adlt is enable
      speicalFareOfStudent();
    } else if (props.specialFareForPax == umnrSpecialFareText) {
      specialFareForUNMR();
    } else if (props.specialFareForPax === seniorCitizenSpecialFareText) {
      specialFareForSNR();
    } else if (props.prevSpecialFareForPax?.prev && !props.prevSpecialFareForPax.current) {
      resetPaxTypeCount();
    }
  };

  useEffect(() => {
    checkforSpecialFare();
  }, [props.selectedSpecialFare]);

  const validateAction = () => {
    const extraSeatCount = paxTypesCount?.EXST?.value || 0;

    if (
      paxTypesCount.ADT.value + paxTypesCount.SNR.value + paxTypesCount.CHD.value + extraSeatCount >=
      TOTAL_PAX_COUNT_LIMIT
    ) {
      paxTypesCount.INF.isIncDisable = true;
      paxTypesCount.ADT.isIncDisable = true;
      paxTypesCount.CHD.isIncDisable = true;
      paxTypesCount.SNR.isIncDisable = true;
    } else {
      // setting the SNR value to false if any SF is selected
      if (
        studentSpecialFareText !== props.specialFareForPax &&
        props.specialFareForPax !== armedForceSpeicalFareText &&
        props.specialFareForPax !== medicalProSpecialFareText &&
        umnrSpecialFareText !== props.specialFareForPax
      ) {
        paxTypesCount.SNR.isIncDisable = false;
      }

      // if SF is UMNR enabling child increment and disabling other increments
      if (umnrSpecialFareText === props.specialFareForPax) {
        paxTypesCount.INF.isIncDisable = true;
        paxTypesCount.ADT.isIncDisable = true;
        paxTypesCount.CHD.isIncDisable = false;
        paxTypesCount.SNR.isIncDisable = true;

        if (paxTypesCount.CHD.value === 1) {
          paxTypesCount.CHD.isDescDisable = true;
        }
        if (paxTypesCount.CHD.value >= UNACCOMPANIED_MINOR_LIMIT) {
          paxTypesCount.CHD.isIncDisable = true;
        }
      } else if (seniorCitizenSpecialFareText === props.specialFareForPax) {
        // if SF is SNR enabling senior increment and disabling other increments
        paxTypesCount.INF.isIncDisable = false;
        paxTypesCount.ADT.isIncDisable = false;
        paxTypesCount.CHD.isIncDisable = false;
        paxTypesCount.SNR.isIncDisable = false;

        if (paxTypesCount.SNR.value === 1) {
          paxTypesCount.SNR.isDescDisable = true;
        }
      } else {
        paxTypesCount.INF.isIncDisable = false;
        paxTypesCount.ADT.isIncDisable = false;
        if (paxTypesCount.CHD.value + extraSeatCount === TOTAL_PAX_COUNT_LIMIT) {
          paxTypesCount.CHD.isIncDisable = true;
        } else {
          paxTypesCount.CHD.isIncDisable = false;
        }
      }
    }
    /*
    1. if inf count should not be more than adt+ snr
    2. child should not be more then 4 at any given point of time
    3. inf should not be more then 4 at any given piont of time
    */

    if (paxTypesCount.ADT.value + paxTypesCount.SNR.value === paxTypesCount.INF.value) {
      paxTypesCount.INF.isIncDisable = true;
    }
    if (paxTypesCount.ADT.value + paxTypesCount.SNR.value < paxTypesCount.INF.value) {
      paxTypesCount.INF.isIncDisable = true;
      paxTypesCount.INF.value = paxTypesCount.ADT.value + paxTypesCount.SNR.value;
    }
    if (paxTypesCount.INF.value > INFANT_UPPER_LIMIT) {
      paxTypesCount.INF.value = INFANT_UPPER_LIMIT;
    }
    if (
      paxTypesCount.ADT.value + paxTypesCount.SNR.value > paxTypesCount.INF.value &&
      paxTypesCount.ADT.value + paxTypesCount.SNR.value + paxTypesCount.CHD.value + extraSeatCount >=
        TOTAL_PAX_COUNT_LIMIT
    ) {
      paxTypesCount.INF.isIncDisable = false;
    }
    if (paxTypesCount.EXST) {
      const maxExtraSeats =
        (paxTypesCount.ADT?.value || 0) + (paxTypesCount.SNR?.value || 0) + (paxTypesCount.CHD?.value || 0);

      if (extraSeatCount > maxExtraSeats) {
        paxTypesCount.EXST.value = maxExtraSeats;
      }

      paxTypesCount.EXST.isIncDisable =
        extraSeatCount === maxExtraSeats ||
        paxTypesCount.ADT.value + paxTypesCount.SNR.value + paxTypesCount.CHD.value + extraSeatCount >=
          TOTAL_PAX_COUNT_LIMIT;
    }

    if (props.specialFareForPax === "") {
      // for default user : without special price

      const maxCapForChild =
        paxTypesCount.ADT.value + paxTypesCount.SNR.value > INFANT_UPPER_LIMIT
          ? INFANT_UPPER_LIMIT
          : paxTypesCount.ADT.value + paxTypesCount.SNR.value;

      if (paxTypesCount.INF.value >= maxCapForChild) {
        paxTypesCount.INF.isIncDisable = true;
      } else {
        paxTypesCount.INF.isIncDisable = false;
      }

      if (paxTypesCount.INF.value > paxTypesCount.ADT.value + paxTypesCount.SNR.value) {
        paxTypesCount.INF.value = paxTypesCount.ADT.value + paxTypesCount.SNR.value;
      }
      if (paxTypesCount.ADT.value + paxTypesCount.SNR.value + paxTypesCount.CHD.value === 0) {
        paxTypesCount.ADT.value = 1;
        paxTypesCount.ADT.isIncDisable = false;
        paxTypesCount.ADT.isDescDisable = false;
      }
    }
    if (props.specialFareForPax != umnrSpecialFareText) {
      // for default user : without special price
      const maxCapForChild =
        paxTypesCount.ADT.value + paxTypesCount.SNR.value > INFANT_UPPER_LIMIT
          ? INFANT_UPPER_LIMIT
          : paxTypesCount.ADT.value + paxTypesCount.SNR.value;

      if (paxTypesCount.INF.value >= maxCapForChild) {
        paxTypesCount.INF.isIncDisable = true;
      } else {
        paxTypesCount.INF.isIncDisable = false;
      }

      if (paxTypesCount.INF.value > paxTypesCount.ADT.value + paxTypesCount.SNR.value) {
        paxTypesCount.INF.value = paxTypesCount.ADT.value + paxTypesCount.SNR.value;
      }
      if (paxTypesCount.ADT.value + paxTypesCount.SNR.value === 0 && props.specialFareForPax !== umnrSpecialFareText) {
        paxTypesCount.ADT.value = 1;
        paxTypesCount.ADT.isIncDisable = false;
        paxTypesCount.ADT.isDescDisable = false;
      }
    }
    // adt : inc or dec enble and disbled

    if (paxTypesCount.ADT.value === 0 && props.specialFareForPax !== umnrSpecialFareText) {
      // adt can be zero at two case, either snr is there, or umnr ;
      if (paxTypesCount.SNR.value > 0 || props.specialFareForPax === umnrSpecialFareText) {
        // paxTypesCount['ADT']['value'] = 1;
      }
      paxTypesCount.ADT.isIncDisable = false;
    }
    if (paxTypesCount.ADT.value == 1) {
      // adt can be zero at two case, either snr is there, or umnr ;
      if (paxTypesCount.SNR.value > 0 || props.specialFareForPax === umnrSpecialFareText) {
        paxTypesCount.ADT.isDescDisable = false;
      } else {
        paxTypesCount.ADT.isDescDisable = true;
      }
    }
    if (props.specialFareForPax === umnrSpecialFareText && paxTypesCount.CHD.value === 0) {
      paxTypesCount.CHD.isDescDisable = true;
      paxTypesCount.CHD.isIncDisable = false;
      paxTypesCount.CHD.value = 1;
    }

    if (
      props.specialFareForPax == umnrSpecialFareText &&
      paxTypesCount.CHD.value + extraSeatCount == TOTAL_PAX_COUNT_LIMIT
    ) {
      paxTypesCount.CHD.isDescDisable = false;
      paxTypesCount.CHD.isIncDisable = true;
    }
    setPaxTypesCount(paxTypesCount);
    setUpdateCount(updateCount + 1); // just to make the view update
  };

  const updatePaxCount = (paxType, action) => {
    if (action === "ADD") {
      paxTypesCount[paxType].value++;
      setTotalPaxCount(totalPaxCount + 1);
      if (paxTypesCount[paxType].value >= 1) {
        paxTypesCount[paxType].isDescDisable = false;
      }
    }
    if (action === "REMOVE") {
      if (paxTypesCount[paxType].value > 0) {
        paxTypesCount[paxType].value--;
        setTotalPaxCount(totalPaxCount - 1);
        if (paxTypesCount[paxType].value == 0) {
          paxTypesCount[paxType].isDescDisable = true;
        }
      } else {
        paxTypesCount[paxType].isDescDisable = true;
      }
    }
    validateAction();
    setPaxTypesCount(paxTypesCount);
    setUpdateCount(updateCount + 1); // just to make the view update
  };

  useEffect(() => {
    validateAction();
  }, [totalPaxCount]);

  useEffect(() => {
    if (enableExtraSeat) {
      setPaxTypesCountLocal({
        ADT: {
          ...paxTypesCountGlobal?.ADT,
          ageGroup: extractResource(resourceData, "adults"),
          ageInfo: extractResource(resourceData, "adultAgeGroup")
        },
        CHD: {
          ...paxTypesCountGlobal?.CHD,
          ageGroup: extractResource(resourceData, "children"),
          ageInfo: extractResource(resourceData, "childrenAgeGroup")
        },
        SNR: {
          ...paxTypesCountGlobal?.SNR,
          ageGroup: extractResource(resourceData, "seniorCitizenPassengerType"),
          ageInfo: extractResource(resourceData, "seniorCitizenAgeGroup")
        },
        INF: {
          ...paxTypesCountGlobal?.INF,
          ageGroup: extractResource(resourceData, "infants"),
          ageInfo: extractResource(resourceData, "infantAgeGroup")
        },
        EXST: {
          ...paxTypesCountGlobal?.EXST,
          ageGroup: extractResource(resourceData, "extraSeats"),
          ageInfo: extractResource(resourceData, "bookTheExtraSeatNextToYou")
        }
      });
    } else {
      setPaxTypesCountLocal({
        ADT: {
          ...paxTypesCountGlobal?.ADT,
          ageGroup: extractResource(resourceData, "adults"),
          ageInfo: extractResource(resourceData, "adultAgeGroup")
        },
        CHD: {
          ...paxTypesCountGlobal?.CHD,
          ageGroup: extractResource(resourceData, "children"),
          ageInfo: extractResource(resourceData, "childrenAgeGroup")
        },
        SNR: {
          ...paxTypesCountGlobal?.SNR,
          ageGroup: extractResource(resourceData, "seniorCitizenPassengerType"),
          ageInfo: extractResource(resourceData, "seniorCitizenAgeGroup")
        },
        INF: {
          ...paxTypesCountGlobal?.INF,
          ageGroup: extractResource(resourceData, "infants"),
          ageInfo: extractResource(resourceData, "infantAgeGroup")
        }
      });
    }

    const finalString = getFinalString(paxTypesCountGlobal);
    setValue(finalString);
  }, [props?.paxTypesCount, enableExtraSeat]);

  return (
    <Popover
      onClose={handleCancel}
      TransitionProps={{ onEntering: handleEntering }}
      open={open}
      {...other}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left"
      }}
      sx={{ maxWidth: "90%" }}
    >
      <Box
        sx={{
          padding: "8px 16px 8px 16px",
          width: "400px",
          "@media(max-width: 768px)": {
            maxWidth: "100% "
          }
        }}
      >
        {Object.keys(paxTypesCount).map((paxType, index) => (
          <PaxCategories
            key={uuidv4(index)}
            {...paxTypesCount[paxType]}
            updatePaxCount={updatePaxCount}
            paxType={paxType}
            index={index}
          />
        ))}
      </Box>
      <Divider />
      <Box
        sx={{
          padding: "8px 16px 8px 16px"
        }}
        className="flex justify-end"
      >
        <Button
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleOk(e)}
          variant="contained"
          color="primary"
          type="button"
          aria-label={extractResource(resourceData, "done")}
        >
          {extractResource(resourceData, "done")}
        </Button>
      </Box>
    </Popover>
  );
}

export default function Passengers(props) {
  const { resourceData, label } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState(`${1} Adult`);
  const [openPopup, setOpenPopup] = useState(false);
  const handleClickListItem = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenPopup(!openPopup);
  };

  const handleClose = (newValue: any) => {
    setAnchorEl(null);
    setOpenPopup(!openPopup);
    if (newValue) {
      setValue(newValue);
    }
  };
  return (
    <Box sx={{ width: "100%" }} className="max-w-100">
      <ListItem button divider aria-haspopup="true" onClick={handleClickListItem}>
        <>
          <ListItemText
            // primary="Passengers"
            primary={
              <div className="pb-[4px] text-typography-secondary text-sm">{label || extractResource(resourceData, "passengers")}</div>
            }
            secondary={<div className="font-medium text-base text-typography-primary pb-2">{value}</div>}
            sx={{
              fontWeight: "bolder",
              "& .MuiListItemText-secondary": {
                textOverflow: "ellipsis",
                width: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                paddingBottom: "5px"
              }
            }}
            className={`${style.passenger} ellipsisText`}
          />
          <Image src={orangeDownArrow} alt="Down Arrow" />
        </>
      </ListItem>
      <ConfirmationDialogRaw
        key={"key_pax_" + resourceData._uid}
        open={openPopup}
        onClose={handleClose}
        anchorEl={anchorEl}
        value={value}
        setValue={setValue}
        specialFareForPax={props.specialFareForPax}
        selectedSpecialFare={props.selectedSpecialFare}
        setPassenger={props.setPassenger}
        resourceData={resourceData}
        passenger={props.passenger}
        paxTypesCount={props.paxTypesCount} // for retaining the enable and disable buttons in pax details popup
        setPaxTypesCount={props.setPaxTypesCount} // for retaining the enable and disable buttons in pax details popup
        prevSpecialFareForPax={props.prevSpecialFareForPax}
        setSpecialFareForPax={props.setSpecialFareForPax}
      />
    </Box>
  );
}
